// src/pages/AccessControlPage.jsx
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Search, Check, CheckCheck, Copy, Trash2, Users,
  Save, LayoutDashboard, FileText, MapPin, Loader2, X,
  Globe, ChevronRight, Database, BarChart3,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { apiCall } from "@/api/apiCall";
import ErrorState from "@/components/common/ErrorStateComponent";
import { usePermissions } from "@/context/PermissionContext";
import { toast } from "sonner";

const BOARDPACK_OPTIONS = [
  { label: "ALL COMPANIES",           value: "ALL COMPANIES"          },
  { label: "Jeddah Airport Contract", value: "Jeddah Airport Contract"},
  { label: "Sharjah Airport Parking", value: "Sharjah Airport Parking"},
  { label: "Airport Jordan",          value: "Airport Jordan"         },
  { label: "Lovers Mkt Off Str-dam",  value: "Lovers Mkt Off Str-dam" },
  { label: "Al Batha Contract",       value: "Al Batha Contract"      },
  { label: "Dammam Airport Contract", value: "Dammam Airport Contract"},
  { label: "TOTAL BILAL PROJECT",     value: "TOTAL BILAL PROJECT"    },
  { label: "Bab Al-Ree Contract",     value: "Bab Al-Ree Contract"    },
  { label: "Gazzaz Contract",         value: "Gazzaz Contract"        },
  { label: "Jed Watch Mkt-parking",   value: "Jed Watch Mkt-parking"  },
  { label: "TOTAL DUBAI",             value: "TOTAL DUBAI"            },
  { label: "Haram Contract",          value: "Haram Contract"         },
  { label: "Taif Contract",           value: "Taif Contract"          },
  { label: "Khamis Contract",         value: "Khamis Contract"        },
  { label: "TOTAL JUBAIL",            value: "TOTAL JUBAIL"           },
  { label: "Hassa Contract",          value: "Hassa Contract"         },
  { label: "Digital City Contract",   value: "Digital City Contract"  },
  { label: "Baha Contract",           value: "Baha Contract"          },
  { label: "Dammam Causeway",         value: "Dammam Causeway"        },
  { label: "Baljurashi Contract",     value: "Baljurashi Contract"    },
  { label: "Makkah Contract",         value: "Makkah Contract"        },
  { label: "Jeddah Contract",         value: "Jeddah Contract"        },
  { label: "MOC Contract",            value: "MOC Contract"           },
  { label: "Modern City Contract",    value: "Modern City Contract" },
  { label: "CAPITAL PARKING CO. LTD",    value: "CAPITAL PARKING CO. LTD" },
];

// ─── Module config ────────────────────────────────────────────────────────────
const MODULES = [
  {
    key: "dashboard",
    label: "Dashboard",
    Icon: LayoutDashboard,
    children: [
      { key: "pl",  label: "P&L",          apiKey: "profit_&_loss", contractSource: "financial" },
      { key: "bs",  label: "Balance Sheet", apiKey: "balance_sheet", contractSource: "financial" },
      { key: "cf",  label: "Cash Flow",     apiKey: "cashflow",      contractSource: "financial" },
    ],
  },
  {
    key: "boardpack",
    label: "Boardpack",
    Icon: FileText,
    children: [{ key: "bp", label: "Boardpack", apiKey: "boardpack", contractSource: "boardpack" }],
  },
  {
    key: "pnd",
    label: "Map Overview",
    Icon: MapPin,
    children: [
      { key: "onstreet",  label: "On Street Overview",  apiKey: "onstreet",  contractSource: "pnd" },
      { key: "offstreet", label: "Off Street Overview",  apiKey: "offstreet", contractSource: "offstreet" },
    ],
  },
  {
    key: "cubes",
    label: "Cube Module",
    Icon: Database,
    children: [{ key: "cubes", label: "TM1 Cubes", apiKey: "tm1_cubes", contractSource: "tm1" }],
  },
];

const ALL_PERM_KEYS = MODULES.flatMap(m => m.children.map(c => c.key));

const ROLE_STYLES = {
  Admin:   "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  Manager: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  Editor:  "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  Viewer:  "bg-muted text-muted-foreground",
};

const LEFT_MIN     = 180;
const LEFT_MAX     = 400;
const LEFT_DEFAULT = 256;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function deepCopy(obj) { return JSON.parse(JSON.stringify(obj)); }

function getInitials(name = "") {
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

function mkEmptyPerms() {
  return Object.fromEntries(ALL_PERM_KEYS.map(k => [k, []]));
}

function getServerCubes(server) {
  return (server?.data?.value || []).filter(cube => !/^[{}]/.test(cube?.Name));
}

function mapApiPermsToInternal(apiPerms = {}) {
  return {
    pl:        apiPerms["profit_&_loss"] ?? [],
    bs:        apiPerms["balance_sheet"] ?? [],
    cf:        apiPerms["cashflow"]      ?? [],
    bp:        apiPerms["boardpack"]     ?? [],
    onstreet:  apiPerms["onstreet"]      ?? [],
    offstreet: (apiPerms["offstreet"] ?? []).map(c => 
      typeof c === "string" ? { name: c, parent: null } : c
    ),
    // Current Status - stored as flat "COUNTRY::CITY::OPS" path strings
    current_status: (() => {
      const raw = apiPerms["current_status"] ?? [];
      if (raw.length > 0 && typeof raw[0] === "string") return raw;

      const paths = [];
      raw.forEach(country => {
        if (country?.assigned) paths.push(country.country_name);
        (country?.cities ?? []).forEach(city => {
          if (city?.assigned) paths.push(`${country.country_name}::${city.city_name}`);
          (city?.ops_list ?? []).forEach(op => {
            if (op?.assigned) paths.push(`${country.country_name}::${city.city_name}::${op.ops}`);
          });
        });
      });
      return paths;
    })(),
    cubes: (apiPerms["tm1_cubes"] ?? []).map(c =>
      typeof c === "string" ? c : JSON.stringify({ server: c.server, name: c.name })
    ),
  };
}

function totalPerms(u) {
  return Object.values(u.perms).reduce((a, v) => a + v.length, 0);
}

function modPermCount(u, mod) {
  return mod.children.reduce((a, c) => a + (u.perms[c.key]?.length ?? 0), 0);
}

// ─── Avatar ───────────────────────────────────────────────────────────────────
function Avatar({ initials, size = "md", className }) {
  const sizes = { sm: "h-7 w-7 text-[10px]", md: "h-8 w-8 text-xs", lg: "h-10 w-10 text-sm" };
  return (
    <div className={cn(
      "rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold shrink-0 select-none",
      sizes[size], className,
    )}>
      {initials}
    </div>
  );
}

// ─── RoleBadge ────────────────────────────────────────────────────────────────
function RoleBadge({ role }) {
  return (
    <span className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full", ROLE_STYLES[role] ?? ROLE_STYLES.Viewer)}>
      {role}
    </span>
  );
}

// ─── ResizeHandle ─────────────────────────────────────────────────────────────
function ResizeHandle({ onResize }) {
  const dragging = useRef(false);

  const onMouseDown = (e) => {
    e.preventDefault();
    dragging.current = true;
    document.body.style.cursor     = "col-resize";
    document.body.style.userSelect = "none";

    const onMouseMove = (e) => { if (dragging.current) onResize(e.clientX); };
    const onMouseUp   = () => {
      dragging.current = false;
      document.body.style.cursor     = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup",   onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup",   onMouseUp);
  };

  return (
    <div onMouseDown={onMouseDown} className="w-1 shrink-0 relative cursor-col-resize group flex items-center justify-center z-10">
      <div className="absolute inset-y-0 -left-1 -right-1" />
      <div className="w-px h-full bg-border group-hover:bg-primary/40 group-active:bg-primary transition-colors" />
      <div className="absolute top-1/2 -translate-y-1/2 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        {[0, 1, 2].map(i => <div key={i} className="h-1 w-1 rounded-full bg-muted-foreground" />)}
      </div>
    </div>
  );
}

// ─── UserList ─────────────────────────────────────────────────────────────────
function UserList({ users, selectedId, onSelect, loading, error, panelWidth }) {
  const [search, setSearch] = useState("");
  const collapsed = panelWidth < 200;

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.role.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full bg-card">
      <div className={cn("border-b border-border shrink-0", collapsed ? "px-2 pt-3 pb-2" : "px-4 pt-4 pb-3")}>
        {!collapsed && (
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2.5">Users</p>
        )}
        {!collapsed && (
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users..." className="pl-8 h-8 text-xs" />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        )}
        {collapsed && <div className="flex justify-center"><Search className="h-3.5 w-3.5 text-muted-foreground" /></div>}
      </div>

      <div className="flex-1 overflow-y-auto py-2 px-2">
        {loading ? (
          <div className="flex items-center justify-center h-full gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {!collapsed && <span className="text-xs">Loading...</span>}
          </div>
        ) : error ? (
          <div className="p-2">{!collapsed && <ErrorState error={error} />}</div>
        ) : filtered.length === 0 ? (
          !collapsed && <p className="text-center text-xs text-muted-foreground py-8">No users found</p>
        ) : (
          filtered.map(u => (
            <button
              key={u.id}
              type="button"
              onClick={() => onSelect(u.id)}
              title={collapsed ? `${u.name} — ${u.role}` : undefined}
              className={cn(
                "w-full flex items-center rounded-md text-left transition-colors mb-0.5 border",
                collapsed ? "justify-center p-2" : "gap-2.5 px-3 py-2.5",
                selectedId === u.id ? "bg-primary/10 border-primary/20" : "hover:bg-accent border-transparent",
              )}
            >
              <Avatar initials={u.initials} size="md" />
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate leading-snug">{u.name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <RoleBadge role={u.role} />
                    <span className="text-[10px] text-muted-foreground">{totalPerms(u)} assigned</span>
                  </div>
                </div>
              )}
              {!collapsed && selectedId === u.id && (
                <div className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

// ─── ContractGrid ─────────────────────────────────────────────────────────────
function ContractGrid({ contracts, selValues, onToggle, search }) {
  const { hasPermission } = usePermissions();

  const filtered = contracts.filter(c => c.label.toLowerCase().includes(search.toLowerCase()));

  if (filtered.length === 0) {
    return <p className="text-center text-xs text-muted-foreground py-10">No items match</p>;
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: "6px" }}>
      {filtered.map(c => {
        const on = selValues.includes(c.value);
        return (
          <button
            key={c.value}
            type="button"
            onClick={() => onToggle(c.value)}
            disabled={hasPermission("assign_access_control") ? false : true}
            className={cn(
              "flex items-center gap-1.5 px-2.5 py-2 rounded-md border text-xs font-medium transition-all text-left truncate w-full",
              on
                ? "bg-primary border-primary text-primary-foreground shadow-sm"
                : "bg-background border-border text-muted-foreground hover:border-primary/40 hover:text-foreground",
            )}
          >
            {on
              ? <Check className="h-3 w-3 shrink-0" />
              : <div className="h-3 w-3 shrink-0 border border-current opacity-30" style={{ borderRadius: 0 }} />
            }
            <span className="truncate">{c.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── OffStreetHierarchyGrid ────────────────────────────────────────────────────
function OffStreetHierarchyGrid({ allLocationsOption, groups, selValues, onToggle, onSelectAllOps, search }) {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission("assign_access_control");

  const [expanded, setExpanded] = useState(() => {
    const initial = {};
    groups.forEach(g => {
      const countrySelected = selValues.some(v => v.name === g.value);
      const opsSelected = g.ops.some(o => selValues.some(v => v.name === o.value));
      if (countrySelected || opsSelected) {
        initial[g.value] = true;
      }
    });
    return initial;
  });

  const q = search.trim().toLowerCase();

  const allLocationsMatches = !q || allLocationsOption.label.toLowerCase().includes(q);

  const visibleGroups = groups
    .map(g => {
      const countryMatches = !q || g.label.toLowerCase().includes(q);
      const opsToShow = countryMatches ? g.ops : g.ops.filter(o => o.label.toLowerCase().includes(q));
      if (q && !countryMatches && opsToShow.length === 0) return null;
      return { ...g, opsToShow };
    })
    .filter(Boolean);

  const isExpanded = (name) => (q ? true : !!expanded[name]);
  const toggleExpand = (name) => setExpanded(prev => ({ ...prev, [name]: !prev[name] }));

  const isSelected = (value) => selValues.some(v => v.name === value);

  if (!allLocationsMatches && visibleGroups.length === 0) {
    return <p className="text-center text-xs text-muted-foreground py-10">No items match</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {allLocationsMatches && (
        <button
          type="button"
          onClick={() => onToggle({ name: allLocationsOption.value, parent: null })}
          disabled={!canEdit}
          className={cn(
            "flex items-center gap-2 px-3 py-2.5 rounded-lg border text-xs font-semibold transition-all text-left",
            isSelected(allLocationsOption.value)
              ? "bg-primary border-primary text-primary-foreground shadow-sm"
              : "bg-background border-border text-foreground hover:border-primary/40",
          )}
        >
          {isSelected(allLocationsOption.value)
            ? <Check className="h-3.5 w-3.5 shrink-0" />
            : <div className="h-3.5 w-3.5 shrink-0 border border-current opacity-30" style={{ borderRadius: 0 }} />
          }
          <Globe className="h-3.5 w-3.5 shrink-0" />
          <span>{allLocationsOption.label}</span>
          <span className="ml-auto text-[9px] font-bold opacity-70 uppercase tracking-wide">Full access</span>
        </button>
      )}

      {visibleGroups.map(group => {
        const countryOn      = isSelected(group.value);
        const opsOnCount     = group.ops.filter(o => isSelected(o.value)).length;
        const allOpsSelected = group.ops.length > 0 && opsOnCount === group.ops.length;
        const exp            = isExpanded(group.value);
        const opsList        = q ? group.opsToShow : group.ops;

        return (
          <div key={group.value} className="border border-border rounded-lg overflow-hidden">
            <div
              className="flex items-center bg-muted/40 cursor-pointer select-none"
              onClick={() => toggleExpand(group.value)}
            >
              <div className="p-2 pl-2.5 text-muted-foreground shrink-0">
                <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", exp && "rotate-90")} />
              </div>

              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggle({ name: group.value, parent: allLocationsOption.value }); }}
                disabled={!canEdit}
                title={countryOn ? "Remove whole-country access" : "Grant whole-country access (all current + future ops)"}
                className="p-1.5 shrink-0"
              >
                {countryOn
                  ? <Check className="h-3.5 w-3.5 text-primary" />
                  : <div className="h-3.5 w-3.5 border border-current opacity-40" style={{ borderRadius: 0 }} />
                }
              </button>

              <div className="flex-1 flex items-center gap-1.5 py-2 pr-3 min-w-0">
                <span className={cn("text-xs font-semibold truncate", countryOn ? "text-primary" : "text-foreground")}>
                  {group.label}
                </span>
                <span className="ml-auto text-[9px] font-bold text-muted-foreground shrink-0 pl-2">
                  {countryOn
                    ? "Whole country"
                    : `${group.ops.length} ops${opsOnCount > 0 ? ` · ${opsOnCount} selected` : ""}`}
                </span>
              </div>
            </div>

            {exp && (
              opsList?.length > 0 ? (
                <div className="pt-1.5 pb-2">
                  {canEdit && (
                    <div className="flex items-center justify-end px-2 pl-8 mb-1">
                      <button
                        type="button"
                        onClick={() => onSelectAllOps(group.ops.map(o => ({ name: o.value, parent: group.value })), !allOpsSelected)}
                        className="text-[10px] font-semibold text-primary hover:underline shrink-0"
                      >
                        {allOpsSelected ? "Clear all" : "Select all"}
                      </button>
                    </div>
                  )}
                  <div
                    className="px-2 pl-8"
                    style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "6px" }}
                  >
                    {opsList.map(op => {
                      const on = isSelected(op.value);
                      return (
                        <button
                          key={op.value}
                          type="button"
                          onClick={() => onToggle({ name: op.value, parent: group.value })}
                          disabled={!canEdit}
                          className={cn(
                            "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-[11px] font-medium transition-all text-left truncate",
                            on
                              ? "bg-primary/90 border-primary text-primary-foreground"
                              : "bg-background border-border text-muted-foreground hover:border-primary/40 hover:text-foreground",
                          )}
                        >
                          {on
                            ? <Check className="h-3 w-3 shrink-0" />
                            : <div className="h-3 w-3 shrink-0 border border-current opacity-30" style={{ borderRadius: 0 }} />
                          }
                          <span className="truncate">{op.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-center text-[11px] text-muted-foreground py-3">No ops in this country</p>
              )
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── CurrentStatusGrid ──────────────────────────────────────────────────────
// Fully independent select: country / city / ops teeno alag alag markers hain
function CurrentStatusGrid({ data, selValues, onToggle, onSelectAll, search, loading, error }) {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission("assign_access_control");

  const [expanded, setExpanded] = useState(() => {
    const initial = {};
    data.forEach(country => {
      const anySelected = selValues.some(v => v === country.country_name || v.startsWith(`${country.country_name}::`));
      if (anySelected) initial[country.country_name] = true;
    });
    return initial;
  });

  const q = search.trim().toLowerCase();

  // "All Locations" — API se nahi aata, isliye hardcoded top-level marker hai
  const isAllLocationsSelected = selValues.includes("All Locations");
  const toggleAllLocations = () => onToggle({ allLocations: true }, isAllLocationsSelected);
  const allLocationsMatches = !q || "all locations".includes(q);

  const visibleData = data
    .map(country => {
      const countryMatches = !q || country.country_name.toLowerCase().includes(q);
      const cities = country.cities || [];

      const visibleCities = cities
        .map(city => {
          const cityMatches = !q || city.city_name.toLowerCase().includes(q);
          const opsList = city.ops_list || [];
          const visibleOps = opsList.filter(op =>
            !q || op.ops_name?.toLowerCase().includes(q) || op.ops?.includes(q)
          );

          if (q && !cityMatches && visibleOps.length === 0) return null;
          return { ...city, opsToShow: visibleOps, cityMatches };
        })
        .filter(Boolean);

      if (q && !countryMatches && visibleCities.length === 0) return null;
      return { ...country, citiesToShow: visibleCities };
    })
    .filter(Boolean);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32 gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-xs">Loading current status...</span>
      </div>
    );
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  if (visibleData.length === 0 && !allLocationsMatches) {
    return <p className="text-center text-xs text-muted-foreground py-10">No items match your search</p>;
  }

  const isExpanded = (key) => (q ? true : !!expanded[key]);
  const toggleExpand = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  // ── Independent marker checks — kisi child/parent completeness pe depend nahi ──
  const isCountrySelected = (country) =>
    selValues.includes(country.country_name);

  const isCitySelected = (countryName, city) =>
    selValues.includes(`${countryName}::${city.city_name}`);

  const isOpSelected = (countryName, cityName, ops) =>
    selValues.includes(`${countryName}::${cityName}::${ops}`);

  // ── Toggle handlers — sirf apna khud ka marker chhedte hain ──
  const toggleCountry = (country) => {
    const selected = isCountrySelected(country);
    onToggle({ country_name: country.country_name }, selected);
  };

  const toggleCity = (countryName, city) => {
    const selected = isCitySelected(countryName, city);
    onToggle({ country_name: countryName, city_name: city.city_name }, selected);
  };

  const toggleOps = (countryName, cityName, ops) => {
    const selected = isOpSelected(countryName, cityName, ops);
    onToggle({ country_name: countryName, city_name: cityName, ops }, selected);
  };

  return (
    <div className="flex flex-col gap-3">
      {allLocationsMatches && (
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="flex items-center bg-muted/40 select-none">
            <div className="p-2 pl-2.5 shrink-0 w-[26px]" />
            <button
              type="button"
              onClick={toggleAllLocations}
              disabled={!canEdit}
              className="p-1.5 shrink-0"
            >
              {isAllLocationsSelected
                ? <Check className="h-3.5 w-3.5 text-primary" />
                : <div className="h-3.5 w-3.5 border border-current opacity-40" style={{ borderRadius: 0 }} />
              }
            </button>
            <div className="flex-1 flex items-center gap-1.5 py-2 pr-3 min-w-0">
              <Globe className="h-3.5 w-3.5 text-blue-500 shrink-0" />
              <span className={cn("text-xs font-semibold truncate", isAllLocationsSelected ? "text-primary" : "text-foreground")}>
                All Locations
              </span>
              <span className="ml-auto text-[9px] font-bold text-muted-foreground shrink-0 pl-2">
                {isAllLocationsSelected ? "Selected" : "Top-level marker"}
              </span>
            </div>
          </div>
        </div>
      )}
      {visibleData.map(country => {
        const countryKey = `country-${country.country_name}`;
        const countryExp = isExpanded(countryKey);
        const cities = country.citiesToShow || [];
        const countrySelected = isCountrySelected(country);
        const totalOps = country.cities?.reduce((acc, c) => acc + (c.ops_list?.length || 0), 0) || 0;
        const selectedOpsCount = country.cities?.reduce((acc, city) => {
          return acc + (city.ops_list?.filter(op =>
            isOpSelected(country.country_name, city.city_name, op.ops)
          ).length || 0);
        }, 0) || 0;
        const selectedCitiesCount = country.cities?.filter(city =>
          isCitySelected(country.country_name, city)
        ).length || 0;

        return (
          <div key={countryKey} className="border border-border rounded-lg overflow-hidden">
            {/* Country header */}
            <div className="flex items-center bg-muted/40 select-none">
              <div
                className="p-2 pl-2.5 text-muted-foreground shrink-0 cursor-pointer"
                onClick={() => toggleExpand(countryKey)}
              >
                <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", countryExp && "rotate-90")} />
              </div>

              {/* Country checkbox — sirf country ka apna marker */}
              <button
                type="button"
                onClick={() => toggleCountry(country)}
                disabled={!canEdit}
                className="p-1.5 shrink-0"
              >
                {countrySelected
                  ? <Check className="h-3.5 w-3.5 text-primary" />
                  : <div className="h-3.5 w-3.5 border border-current opacity-40" style={{ borderRadius: 0 }} />
                }
              </button>

              <div className="flex-1 flex items-center gap-1.5 py-2 pr-3 min-w-0">
                <Globe className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                <span className={cn("text-xs font-semibold truncate", countrySelected ? "text-primary" : "text-foreground")}>
                  {country.country_name}
                </span>
                <span className="ml-auto text-[9px] font-bold text-muted-foreground shrink-0 pl-2">
                  {countrySelected
                    ? "Country selected"
                    : `${totalOps} ops${selectedOpsCount > 0 ? ` · ${selectedOpsCount} selected` : ""}${selectedCitiesCount > 0 ? ` · ${selectedCitiesCount} city sel.` : ""}`}
                </span>
              </div>
            </div>

            {/* Cities */}
            {countryExp && (
              <div className="p-3 pt-1 space-y-2">
                {cities.map(city => {
                  const cityKey = `city-${country.country_name}-${city.city_name}`;
                  const cityExp = isExpanded(cityKey);
                  const ops = city.opsToShow || city.ops_list || [];
                  const citySelected = isCitySelected(country.country_name, city);
                  const citySelectedOpsCount = ops.filter(op =>
                    isOpSelected(country.country_name, city.city_name, op.ops)
                  ).length;

                  return (
                    <div key={cityKey} className="border border-border rounded-md overflow-hidden">
                      <div className="flex items-center bg-muted/20 select-none">
                        <div
                          className="p-1.5 pl-2.5 text-muted-foreground shrink-0 cursor-pointer"
                          onClick={() => toggleExpand(cityKey)}
                        >
                          <ChevronRight className={cn("h-3 w-3 transition-transform", cityExp && "rotate-90")} />
                        </div>

                        {/* City checkbox — sirf city ka apna marker */}
                        <button
                          type="button"
                          onClick={() => toggleCity(country.country_name, city)}
                          disabled={!canEdit}
                          className="p-1 shrink-0"
                        >
                          {citySelected
                            ? <Check className="h-3 w-3 text-primary" />
                            : <div className="h-3 w-3 border border-current opacity-40" style={{ borderRadius: 0 }} />
                          }
                        </button>

                        <div className="flex-1 flex items-center gap-1.5 py-1.5 pr-3 min-w-0">
                          <MapPin className="h-3 w-3 text-amber-500 shrink-0" />
                          <span className={cn("text-[11px] font-medium truncate", citySelected ? "text-primary" : "text-foreground")}>
                            {city.city_name}
                          </span>
                          <span className="ml-auto text-[9px] font-bold text-muted-foreground shrink-0 pl-2">
                            {citySelected
                              ? "City selected"
                              : `${ops.length} ops${citySelectedOpsCount > 0 ? ` · ${citySelectedOpsCount} selected` : ""}`}
                          </span>
                        </div>
                      </div>

                      {/* Ops list */}
                      {cityExp && (
                        <div className="p-2 pl-8 grid grid-cols-2 gap-1.5">
                          {ops.map(op => {
                            const selected = isOpSelected(country.country_name, city.city_name, op.ops);
                            return (
                              <button
                                key={op.ops}
                                type="button"
                                onClick={() => toggleOps(country.country_name, city.city_name, op.ops)}
                                disabled={!canEdit}
                                className={cn(
                                  "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-[11px] font-medium transition-all text-left truncate",
                                  selected
                                    ? "bg-primary/90 border-primary text-primary-foreground"
                                    : "bg-background border-border text-muted-foreground hover:border-primary/40 hover:text-foreground",
                                )}
                              >
                                {selected
                                  ? <Check className="h-3 w-3 shrink-0" />
                                  : <div className="h-3 w-3 shrink-0 border border-current opacity-30" style={{ borderRadius: 0 }} />
                                }
                                <span className="truncate">{op.ops_name || op.ops}</span>
                              </button>
                            );
                          })}
                          {ops.length === 0 && (
                            <p className="text-[10px] text-muted-foreground col-span-2 py-2 text-center">No ops</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── OffStreetPanel ──────────────────────────────────────────────────────────
function OffStreetPanel({
  offStreetCountries, offStreetCountriesLoading, offStreetCountriesError,
  currentStatusData, currentStatusLoading, currentStatusError,
  offStreetSelValues, currentStatusSelValues,
  onOffStreetToggle, onOffStreetSelectAllOps,
  onCurrentStatusToggle,
  onSelectAllOffStreet,
  onClearOffStreet,
  onSelectAllCurrentStatus,
  onClearCurrentStatus,
  contractSearch,
}) {
  const [activeTab, setActiveTab] = useState("map");

  return (
    <div className="flex flex-col h-full">
      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border shrink-0 px-1">
        <button
          type="button"
          onClick={() => setActiveTab("map")}
          className={cn(
            "px-3 py-2 text-xs font-medium transition-colors border-b-2",
            activeTab === "map"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          <span className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" />
            Off Street Map
          </span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("status")}
          className={cn(
            "px-3 py-2 text-xs font-medium transition-colors border-b-2",
            activeTab === "status"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          <span className="flex items-center gap-1.5">
            <BarChart3 className="h-3.5 w-3.5" />
            Current Status
          </span>
        </button>
      </div>

      {/* Controls - only show for active tab */}
      <div className="flex flex-wrap items-center gap-2 py-2 shrink-0">
        {activeTab === "map" ? (
          // Off Street Map controls
          <>
            <button 
              onClick={onSelectAllOffStreet} 
              className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-border bg-background text-muted-foreground hover:bg-accent transition-colors"
            >
              <CheckCheck className="h-3 w-3" /> Select all
            </button>
            <button 
              onClick={onClearOffStreet} 
              className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-border bg-background text-destructive hover:bg-destructive/10 transition-colors"
            >
              <Trash2 className="h-3 w-3" /> Clear
            </button>
            <div className="ml-auto">
              <span className="text-[11px] text-muted-foreground">
                {offStreetSelValues.length} selected
              </span>
            </div>
          </>
        ) : (
          // Current Status controls
          <>
            <button 
              onClick={onSelectAllCurrentStatus} 
              className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-border bg-background text-muted-foreground hover:bg-accent transition-colors"
            >
              <CheckCheck className="h-3 w-3" /> Select all
            </button>
            <button 
              onClick={onClearCurrentStatus} 
              className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-border bg-background text-destructive hover:bg-destructive/10 transition-colors"
            >
              <Trash2 className="h-3 w-3" /> Clear
            </button>
            <div className="ml-auto">
              <span className="text-[11px] text-muted-foreground">
                {currentStatusSelValues.length} selected
              </span>
            </div>
          </>
        )}
      </div>

      {/* Tab content */}
      <div className="flex-1 min-h-0 overflow-y-auto pb-4">
        {activeTab === "map" ? (
          <OffStreetHierarchyGrid
            allLocationsOption={offStreetCountries.allOption}
            groups={offStreetCountries.groups}
            selValues={offStreetSelValues}
            onToggle={onOffStreetToggle}
            onSelectAllOps={onOffStreetSelectAllOps}
            search={contractSearch}
          />
        ) : (
          <CurrentStatusGrid
            data={currentStatusData}
            selValues={currentStatusSelValues}
            onToggle={onCurrentStatusToggle}
            search={contractSearch}
            loading={currentStatusLoading}
            error={currentStatusError}
          />
        )}
      </div>
    </div>
  );
}

// ─── CubeHierarchyGrid ──────────────────────────────────────────────────────
function CubeHierarchyGrid({ servers, selValues, onToggle, onSelectAllCubes, search, loading }) {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission("assign_access_control");

  const [expanded, setExpanded] = useState(() => {
    const initial = {};
    servers.forEach(s => {
      const serverCubes = getServerCubes(s);
      if (serverCubes.some(cube => {
        const cubeValue = JSON.stringify({
          server: s.server,
          name: cube.Name,
        });
        return selValues.includes(cubeValue);
      })) {
        initial[s.server] = true;
      }
    });
    return initial;
  });

  const q = search.trim().toLowerCase();

  const visibleServers = servers
    .map(s => {
      const serverMatches = !q || s.server.toLowerCase().includes(q);
      const cubes = getServerCubes(s);
      const cubesToShow = serverMatches
        ? cubes 
        : cubes.filter(c => c.Name.toLowerCase().includes(q));
      
      if (q && !serverMatches && cubesToShow.length === 0) return null;
      return { ...s, cubesToShow };
    })
    .filter(Boolean);

  const isExpanded = (serverName) => (q ? true : !!expanded[serverName]);
  const toggleExpand = (serverName) => setExpanded(prev => ({ ...prev, [serverName]: !prev[serverName] }));

  const toggleAllCubes = (server, select) => {
    const cubes = getServerCubes(server);
    const cubeValues = cubes.map(cube => JSON.stringify({
      server: server.server,
      name: cube.Name,
    }));
    
    cubeValues.forEach(v => {
      if (select) {
        if (!selValues.includes(v)) onToggle(v);
      } else {
        if (selValues.includes(v)) onToggle(v);
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32 gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-xs">Loading cubes...</span>
      </div>
    );
  }

  if (visibleServers.length === 0) {
    return <p className="text-center text-xs text-muted-foreground py-10">No cubes match your search</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {visibleServers.map(server => {
        const cubes = server.cubesToShow || getServerCubes(server);
        const allCubeValues = cubes.map(cube => JSON.stringify({
          server: server.server,
          name: cube.Name,
        }));
        
        const selectedCount = allCubeValues.filter(v => selValues.includes(v)).length;
        const allSelected = cubes.length > 0 && selectedCount === cubes.length;
        const exp = isExpanded(server.server);

        return (
          <div key={server.server} className="border border-border rounded-lg overflow-hidden">
            <div
              className="flex items-center bg-muted/40 cursor-pointer select-none"
              onClick={() => toggleExpand(server.server)}
            >
              <div className="p-2 pl-2.5 text-muted-foreground shrink-0">
                <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", exp && "rotate-90")} />
              </div>

              <button
                type="button"
                onClick={(e) => { 
                  e.stopPropagation(); 
                  toggleAllCubes(server, !allSelected);
                }}
                disabled={!canEdit}
                title={allSelected ? "Remove all cubes access" : "Grant access to all cubes on this server"}
                className="p-1.5 shrink-0"
              >
                {allSelected
                  ? <Check className="h-3.5 w-3.5 text-primary" />
                  : <div className="h-3.5 w-3.5 border border-current opacity-40" style={{ borderRadius: 0 }} />
                }
              </button>

              <div className="flex-1 flex items-center gap-1.5 py-2 pr-3 min-w-0">
                <Database className="h-3.5 w-3.5 text-purple-500 shrink-0" />
                <span className={cn("text-xs font-semibold truncate", allSelected ? "text-primary" : "text-foreground")}>
                  {server.server}
                </span>
                <span className="ml-auto text-[9px] font-bold text-muted-foreground shrink-0 pl-2">
                  {allSelected 
                    ? `All ${cubes.length} cubes` 
                    : `${cubes.length} cubes${selectedCount > 0 ? ` · ${selectedCount} selected` : ""}`}
                </span>
              </div>
            </div>

            {exp && (
              cubes.length > 0 ? (
                <div className="pt-1.5 pb-2">
                  {canEdit && (
                    <div className="flex items-center justify-end px-2 pl-8 mb-1">
                      <button
                        type="button"
                        onClick={() => toggleAllCubes(server, !allSelected)}
                        className="text-[10px] font-semibold text-primary hover:underline shrink-0"
                      >
                        {allSelected ? "Clear all" : "Select all"}
                      </button>
                    </div>
                  )}
                  <div
                    className="px-2 pl-8"
                    style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "6px" }}
                  >
                    {cubes.map(cube => {
                      const cubeValue = JSON.stringify({
                        server: server.server,
                        name: cube.Name,
                      });
                      const on = selValues.includes(cubeValue);
                      
                      const displayName = cube.Name.replace(/_/g, ' ');
                      return (
                        <button
                          key={cube.Name}
                          type="button"
                          onClick={() => onToggle(cubeValue)}
                          disabled={!canEdit}
                          className={cn(
                            "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-[11px] font-medium transition-all text-left truncate",
                            on
                              ? "bg-primary/90 border-primary text-primary-foreground"
                              : "bg-background border-border text-muted-foreground hover:border-primary/40 hover:text-foreground",
                          )}
                        >
                          {on
                            ? <Check className="h-3 w-3 shrink-0" />
                            : <div className="h-3 w-3 shrink-0 border border-current opacity-30" style={{ borderRadius: 0 }} />
                          }
                          <span className="truncate">{displayName}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-center text-[11px] text-muted-foreground py-3">No cubes on this server</p>
              )
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── PermissionPanel ──────────────────────────────────────────────────────────
function PermissionPanel({
  user, allUsers,
  financialContracts, financialContractsLoading, financialContractsError,
  pndCities, pndCitiesLoading, pndCitiesError,
  offStreetCountries, offStreetCountriesLoading, offStreetCountriesError,
  currentStatusData, currentStatusLoading, currentStatusError,
  tm1Servers, tm1ServersLoading, tm1ServersError,
  onSave, isSaving,
}) {
  const [perms, setPerms]           = useState(() => deepCopy(user.perms));
  const [activeMod, setActiveMod]   = useState(MODULES[0].key);
  const [activeSub, setActiveSub]   = useState(MODULES[0].children[0].key);
  const [contractSearch, setSearch] = useState("");
  const [saved, setSaved]           = useState(false);
  const [copyOpen, setCopyOpen]     = useState(false);
  const dropRef                     = useRef(null);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    setPerms(deepCopy(user.perms));
    setActiveMod(MODULES[0].key);
    setActiveSub(MODULES[0].children[0].key);
    setSearch("");
    setSaved(false);
  }, [user.id, user.perms]);

  useEffect(() => {
    const h = e => { if (dropRef.current && !dropRef.current.contains(e.target)) setCopyOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const switchMod = mk => {
    setActiveMod(mk);
    setActiveSub(MODULES.find(m => m.key === mk).children[0].key);
    setSearch("");
  };

  const activeSubConfig      = MODULES.flatMap(m => m.children).find(c => c.key === activeSub);
  const activeContractSource = activeSubConfig?.contractSource ?? "financial";

  const tm1Cubes = useMemo(() => {
    if (activeContractSource !== "tm1") return [];
    const allCubes = [];
    tm1Servers.forEach(server => {
      const cubes = getServerCubes(server);
      cubes.forEach(cube => {
        allCubes.push({
          label: cube.Name,
          value: JSON.stringify({
            server: server.server,
            name: cube.Name,
          }),
          _raw: {
            server: server.server,
            name: cube.Name,
          }
        });
      });
    });
    return allCubes;
  }, [tm1Servers, activeContractSource]);

  const activeContracts =
    activeContractSource === "tm1"        ? tm1Cubes :
    activeContractSource === "pnd"        ? pndCities :
    activeContractSource === "offstreet"  ? offStreetCountries.flatValues :
    activeContractSource === "boardpack"  ? BOARDPACK_OPTIONS :
    financialContracts;

  const contractsLoading =
    activeContractSource === "tm1"        ? tm1ServersLoading :
    activeContractSource === "pnd"        ? pndCitiesLoading :
    activeContractSource === "offstreet"  ? offStreetCountriesLoading :
    activeContractSource === "boardpack"  ? false :
    financialContractsLoading;

  const contractsError =
    activeContractSource === "tm1"        ? tm1ServersError :
    activeContractSource === "pnd"        ? pndCitiesError :
    activeContractSource === "offstreet"  ? offStreetCountriesError :
    activeContractSource === "boardpack"  ? null :
    financialContractsError;

  const totalCubes = useMemo(() => {
    if (activeContractSource !== "tm1") return activeContracts.length;
    let count = 0;
    tm1Servers.forEach(server => {
      count += getServerCubes(server).length;
    });
    return count;
  }, [tm1Servers, activeContractSource]);

  // For offstreet - separate toggle functions for map and current status
  const offStreetSelValues = perms["offstreet"] ?? [];
  const currentStatusSelValues = perms["current_status"] ?? [];

  const toggleOffStreet = (contractValue, isRemove) => {
    setPerms(prev => {
      const next = deepCopy(prev);
      const arr = next["offstreet"];
      
      if (isRemove !== undefined) {
        // isRemove true = remove, false = add
        if (isRemove) {
          const index = arr.findIndex(v => v.name === contractValue.name);
          if (index > -1) arr.splice(index, 1);
        } else {
          if (!arr.some(v => v.name === contractValue.name)) {
            arr.push(contractValue);
          }
        }
      } else {
        // Toggle
        const index = arr.findIndex(v => v.name === contractValue.name);
        if (index > -1) arr.splice(index, 1);
        else arr.push(contractValue);
      }
      return next;
    });
    setSaved(false);
  };

  const toggleOffStreetOps = (opsValues, select) => {
    setPerms(prev => {
      const next = deepCopy(prev);
      const arr = next["offstreet"];
      
      if (select) {
        opsValues.forEach(v => {
          if (!arr.some(item => item.name === v.name)) arr.push(v);
        });
      } else {
        opsValues.forEach(v => {
          const index = arr.findIndex(item => item.name === v.name);
          if (index > -1) arr.splice(index, 1);
        });
      }
      return next;
    });
    setSaved(false);
  };

  const toggleCurrentStatus = (contractValue, isRemove) => {
  setPerms(prev => {
    const next = deepCopy(prev);
    const arr = next["current_status"] || [];

    const hasOps  = contractValue.ops !== undefined && contractValue.ops !== null;
    const hasCity = contractValue.city_name !== undefined && contractValue.city_name !== null;

    const path = contractValue.allLocations
      ? "All Locations"
      : hasOps
      ? `${contractValue.country_name}::${contractValue.city_name}::${contractValue.ops}`
      : hasCity
      ? `${contractValue.country_name}::${contractValue.city_name}`
      : contractValue.country_name;

    const idx = arr.indexOf(path);
    if (isRemove) {
      if (idx > -1) arr.splice(idx, 1);
    } else if (idx === -1) {
      arr.push(path);
    }

    next["current_status"] = arr;
    return next;
  });
  setSaved(false);
};

  const selectAllOffStreet = () => {
    if (activeContractSource === "tm1") {
      const allCubeValues = [];
      tm1Servers.forEach(server => {
        const cubes = getServerCubes(server);
        cubes.forEach(cube => {
          allCubeValues.push(JSON.stringify({
            server: server.server,
            name: cube.Name,
          }));
        });
      });
      setPerms(prev => ({ ...deepCopy(prev), [activeSub]: allCubeValues }));
    } else if (activeContractSource === "offstreet") {
      // For offstreet map
      const allOffStreetValues = [
        { name: "ALL_LOCATIONS", parent: null },
        ...offStreetCountries.groups.map(g => ({ name: g.value, parent: "ALL_LOCATIONS" })),
        ...offStreetCountries.groups.flatMap(g => 
          g.ops.map(op => ({ name: op.value, parent: g.value }))
        )
      ];
      setPerms(prev => ({ ...deepCopy(prev), offstreet: allOffStreetValues }));
    } else {
      setPerms(prev => ({ ...deepCopy(prev), [activeSub]: activeContracts.map(c => c.value) }));
    }
    setSaved(false);
  };

  const clearOffStreet = () => {
    setPerms(prev => ({ ...deepCopy(prev), offstreet: [] }));
    setSaved(false);
  };

  const clearCurrentStatus = () => {
    setPerms(prev => ({ ...deepCopy(prev), current_status: [] }));
    setSaved(false);
  };

  // const selectAllCurrentStatus = () => {
  //   // Select all countries, cities, ops from current status data
  //   const allEntries = [];
  //   currentStatusData.forEach(country => {
  //     country.cities?.forEach(city => {
  //       city.ops_list?.forEach(op => {
  //         allEntries.push({
  //           country_name: country.country_name,
  //           city_name: city.city_name,
  //           ops: op.ops
  //         });
  //       });
  //     });
  //   });
    
  //   // Convert to the required format
  //   const groupedByCountry = {};
  //   allEntries.forEach(entry => {
  //     if (!groupedByCountry[entry.country_name]) {
  //       groupedByCountry[entry.country_name] = {};
  //     }
  //     if (!groupedByCountry[entry.country_name][entry.city_name]) {
  //       groupedByCountry[entry.country_name][entry.city_name] = [];
  //     }
  //     groupedByCountry[entry.country_name][entry.city_name].push({ ops: entry.ops });
  //   });

  //   const result = Object.keys(groupedByCountry).map(countryName => ({
  //     country_name: countryName,
  //     cities: Object.keys(groupedByCountry[countryName]).map(cityName => ({
  //       city_name: cityName,
  //       ops_list: groupedByCountry[countryName][cityName]
  //     }))
  //   }));

  //   setPerms(prev => ({ ...deepCopy(prev), current_status: result }));
  //   setSaved(false);
  // };



  const selectAllCurrentStatus = () => {
  // Har country ka independent country-level marker bana dete hain, "All Locations" samet
  const result = ["All Locations", ...currentStatusData.map(country => country.country_name)];
  setPerms(prev => ({ ...deepCopy(prev), current_status: result }));
  setSaved(false);
};
  const toggleContract = (contractValue) => {
    setPerms(prev => {
      const next = deepCopy(prev);
      const arr = next[activeSub];
      
      if (typeof contractValue === 'object' && contractValue.name) {
        const index = arr.findIndex(v => {
          if (typeof v === 'object' && v.name) {
            return v.name === contractValue.name;
          }
          return v === contractValue;
        });
        if (index > -1) arr.splice(index, 1);
        else arr.push(contractValue);
      } else {
        const index = arr.indexOf(contractValue);
        if (index > -1) arr.splice(index, 1);
        else arr.push(contractValue);
      }
      return next;
    });
    setSaved(false);
  };

  const setOpsSelection = (opsValues, select) => {
    setPerms(prev => {
      const next = deepCopy(prev);
      const arr = next[activeSub];
      
      if (select) {
        opsValues.forEach(v => {
          const exists = arr.some(item => {
            if (typeof v === 'object' && v.name) {
              return typeof item === 'object' && item.name && item.name === v.name;
            }
            return item === v;
          });
          if (!exists) arr.push(v);
        });
      } else {
        opsValues.forEach(v => {
          const index = arr.findIndex(item => {
            if (typeof v === 'object' && v.name) {
              return typeof item === 'object' && item.name && item.name === v.name;
            }
            return item === v;
          });
          if (index > -1) arr.splice(index, 1);
        });
      }
      return next;
    });
    setSaved(false);
  };

  const selectAll = () => {
    if (activeContractSource === "tm1") {
      const allCubeValues = [];
      tm1Servers.forEach(server => {
        const cubes = getServerCubes(server);
        cubes.forEach(cube => {
          allCubeValues.push(JSON.stringify({
            server: server.server,
            name: cube.Name,
          }));
        });
      });
      setPerms(prev => ({ ...deepCopy(prev), [activeSub]: allCubeValues }));
    } else if (activeContractSource === "offstreet") {
      selectAllOffStreet();
    } else {
      setPerms(prev => ({ ...deepCopy(prev), [activeSub]: activeContracts.map(c => c.value) }));
    }
    setSaved(false);
  };

  const clearSub = () => {
    if (activeContractSource === "offstreet") {
      clearOffStreet();
    } else {
      setPerms(prev => ({ ...deepCopy(prev), [activeSub]: [] }));
    }
    setSaved(false);
  };

  const copyFromPL   = () => { setPerms(prev => ({ ...deepCopy(prev), [activeSub]: [...prev.pl] })); setSaved(false); };
  const applyPLToAll = () => {
    setPerms(prev => { const next = deepCopy(prev); next.bs = [...prev.pl]; next.cf = [...prev.pl]; return next; });
    setSaved(false);
  };

  const copyFromUser = uid => {
    const src = allUsers.find(u => u.id === uid);
    if (src) setPerms(deepCopy(src.perms));
    setCopyOpen(false);
    setSaved(false);
  };

  const handleSaveClick = async () => {
    await onSave(user.id, perms);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const currentMod = MODULES.find(m => m.key === activeMod);
  const selValues = perms[activeSub] ?? [];

  const renderContractContent = () => {
    if (activeContractSource === "offstreet") {
      return (
        <OffStreetPanel
        offStreetCountries={offStreetCountries}
        offStreetCountriesLoading={offStreetCountriesLoading}
        offStreetCountriesError={offStreetCountriesError}
        currentStatusData={currentStatusData}
        currentStatusLoading={currentStatusLoading}
        currentStatusError={currentStatusError}
        offStreetSelValues={offStreetSelValues}
        currentStatusSelValues={currentStatusSelValues}
        onOffStreetToggle={toggleOffStreet}
        onOffStreetSelectAllOps={toggleOffStreetOps}
        onCurrentStatusToggle={toggleCurrentStatus}
        onSelectAllOffStreet={selectAllOffStreet}
        onClearOffStreet={clearOffStreet}
        onSelectAllCurrentStatus={selectAllCurrentStatus}
        onClearCurrentStatus={clearCurrentStatus}
        contractSearch={contractSearch}
      />
      );
    }

    if (contractsLoading) {
      return (
        <div className="flex items-center justify-center h-32 gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-xs">
            {activeContractSource === "tm1" ? "Loading cubes…" :
             activeContractSource === "pnd" ? "Loading cities…" :
             "Loading contracts…"}
          </span>
        </div>
      );
    }

    if (contractsError) {
      return <ErrorState error={contractsError} />;
    }

    if (activeContractSource === "tm1") {
      return (
        <CubeHierarchyGrid
          servers={tm1Servers}
          selValues={selValues}
          onToggle={toggleContract}
          onSelectAllCubes={setOpsSelection}
          search={contractSearch}
          loading={tm1ServersLoading}
        />
      );
    }

    return (
      <ContractGrid
        contracts={activeContracts}
        selValues={selValues}
        onToggle={toggleContract}
        search={contractSearch}
      />
    );
  };

  return (
    <div className="flex flex-col h-full bg-card">

      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <Avatar initials={user.initials} size="lg" />
          <div>
            <p className="text-sm font-semibold leading-snug">{user.name}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <RoleBadge role={user.role} />
              <span className="text-[10px] text-muted-foreground">{user.email}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {hasPermission("assign_access_control") && (
            <Button
              size="sm"
              onClick={handleSaveClick}
              disabled={isSaving}
              className={cn("h-8 text-xs gap-1.5 transition-colors", saved && "bg-green-700 hover:bg-green-800")}
            >
              {isSaving ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</>
              ) : saved ? (
                <><Check className="h-3.5 w-3.5" /> Saved</>
              ) : (
                <><Save className="h-3.5 w-3.5" /> Save changes</>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Module tabs */}
      <div className="flex items-center gap-1.5 px-5 py-2.5 border-b border-border bg-muted/30 shrink-0 overflow-x-auto">
        {MODULES.map(mod => {
          const { Icon } = mod;
          const cnt      = modPermCount({ perms }, mod);
          const isActive = activeMod === mod.key;
          return (
            <button key={mod.key} type="button" onClick={() => switchMod(mod.key)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors shrink-0 border",
                isActive ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground hover:bg-accent hover:text-foreground",
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {mod.label}
              <span className={cn(
                "text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none",
                isActive ? "bg-white/20 text-white" : "bg-secondary text-secondary-foreground",
              )}>
                {cnt}
              </span>
            </button>
          );
        })}
      </div>

      {/* Sub-tabs */}
      {currentMod.children.length > 1 && (
        <div className="flex items-center gap-2 px-5 pt-3 pb-1 shrink-0">
          {currentMod.children.map(ch => (
            <button key={ch.key} type="button" onClick={() => { setActiveSub(ch.key); setSearch(""); }}
              className={cn(
                "px-4 py-1 rounded-full border text-xs font-medium transition-colors",
                activeSub === ch.key ? "bg-primary/10 border-primary/30 text-primary" : "border-border text-muted-foreground hover:bg-accent",
              )}
            >
              {ch.label}
              <span className="ml-1.5 text-[9px] font-bold opacity-60">{(perms[ch.key] ?? []).length}</span>
            </button>
          ))}
        </div>
      )}

      {/* Contract area */}
      <div className="flex-1 min-h-0 flex flex-col px-5 pt-3 overflow-hidden">

        {hasPermission("assign_access_control") && activeContractSource !== "offstreet" && (
          <div className="flex flex-wrap items-center gap-2 mb-3 shrink-0">
            <button onClick={selectAll} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-border bg-background text-muted-foreground hover:bg-accent transition-colors">
              <CheckCheck className="h-3 w-3" /> Select all
            </button>
            <button onClick={clearSub} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-border bg-background text-destructive hover:bg-destructive/10 transition-colors">
              <Trash2 className="h-3 w-3" /> Clear
            </button>
            {(activeSub === "bs" || activeSub === "cf") && (
              <button onClick={copyFromPL} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-border bg-background text-muted-foreground hover:bg-accent transition-colors">
                <Copy className="h-3 w-3" /> Copy from P&L
              </button>
            )}
            {activeSub === "pl" && (
              <button onClick={applyPLToAll} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-border bg-background text-muted-foreground hover:bg-accent transition-colors">
                <Copy className="h-3 w-3" /> Apply P&L to all sheets
              </button>
            )}
            <div className="ml-auto">
              <span className="text-[11px] text-muted-foreground">
                {selValues.length} / {activeContractSource === "tm1" ? totalCubes : activeContracts.length} selected
              </span>
            </div>
          </div>
        )}

        <div className="relative mb-3 shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={contractSearch}
            onChange={e => setSearch(e.target.value)}
            placeholder={
              activeContractSource === "tm1"       ? "Filter servers or cubes…" :
              activeContractSource === "pnd"       ? "Filter cities…" :
              activeContractSource === "offstreet" ? "Filter locations, countries or ops…" :
              "Filter contracts…"
            }
            className="pl-9 h-8 text-xs"
          />
          {contractSearch && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pb-4">
          {renderContractContent()}
        </div>
      </div>
    </div>
  );
}

// ─── EmptyState ───────────────────────────────────────────────────────────────
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
      <Users className="h-10 w-10 opacity-20" />
      <p className="text-sm">Select a user to manage permissions</p>
    </div>
  );
}

// ─── AccessControlPage ────────────────────────────────────────────────────────
const AccessControlPage = () => {
  const [users, setUsers]                   = useState([]);
  const [usersLoading, setUsersLoading]     = useState(true);
  const [usersError, setUsersError]         = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isSaving, setIsSaving]             = useState(false);

  const [financialContracts, setFinancialContracts]               = useState([]);
  const [financialContractsLoading, setFinancialContractsLoading] = useState(true);
  const [financialContractsError, setFinancialContractsError]     = useState(null);

  const [pndCities, setPndCities]               = useState([]);
  const [pndCitiesLoading, setPndCitiesLoading] = useState(true);
  const [pndCitiesError, setPndCitiesError]     = useState(null);

  const [offStreetCountries, setOffStreetCountries]               = useState({ allOption: { label: "ALL LOCATIONS", value: "ALL_LOCATIONS" }, groups: [], flatValues: [] });
  const [offStreetCountriesLoading, setOffStreetCountriesLoading] = useState(true);
  const [offStreetCountriesError, setOffStreetCountriesError]     = useState(null);

  const [currentStatusData, setCurrentStatusData] = useState([]);
  const [currentStatusLoading, setCurrentStatusLoading] = useState(true);
  const [currentStatusError, setCurrentStatusError] = useState(null);

  const [tm1Servers, setTm1Servers] = useState([]);
  const [tm1ServersLoading, setTm1ServersLoading] = useState(true);
  const [tm1ServersError, setTm1ServersError] = useState(null);

  const [leftWidth, setLeftWidth] = useState(LEFT_DEFAULT);
  const containerRef              = useRef(null);
  const hasFetched                = useRef(false);

  const { fetchUserContracts } = usePermissions();

  const handleResize = useCallback((clientX) => {
    if (!containerRef.current) return;
    const newWidth = Math.min(LEFT_MAX, Math.max(LEFT_MIN, clientX - containerRef.current.getBoundingClientRect().left));
    setLeftWidth(newWidth);
  }, []);

  const fetchFinancialContracts = useCallback(async () => {
    try {
      setFinancialContractsLoading(true);
      const response = await apiCall("get", "/boardpack/contracts");
      if (response?.data?.contracts) {
        setFinancialContracts(response.data.contracts.map(item => ({
          label: item.description,
          value: item.description,
        })));
      }
    } catch (err) {
      setFinancialContractsError("Failed to load contracts");
    } finally {
      setFinancialContractsLoading(false);
    }
  }, []);

  const fetchPndCities = useCallback(async () => {
    try {
      setPndCitiesLoading(true);
      setPndCitiesError(null);
      const response = await apiCall("get", "/cp-map/data", null, null, false, false);
      if (response?.success && Array.isArray(response?.data?.cities)) {
        setPndCities([
          {
            label: response.data.country_name,
            value: response.data.country_name,
          },
          ...response.data.cities.map((city) => ({
            label: city.city_name || city.city_cd,
            value: city.city_send_name || city.city_cd,
          })),
        ]);
      } else {
        setPndCitiesError("Failed to load PND cities");
      }
    } catch (err) {
      setPndCitiesError("Failed to load PND cities");
    } finally {
      setPndCitiesLoading(false);
    }
  }, []);

  const fetchOffStreetCountries = useCallback(async () => {
    try {
      setOffStreetCountriesLoading(true);
      setOffStreetCountriesError(null);
      const response = await apiCall("get", "/cp-map/off-street", null, null, false, false);
      if (response?.success && response?.fields?.data) {
        const topLevel = response.fields.data || [];
        const countries = topLevel.flatMap(item =>
          Array.isArray(item.children) && item.children.length > 0
            ? item.children
            : (item.ops || item.cities) ? [item] : []
        );

        const groups = countries.map(country => {
          const opsList = Array.isArray(country.cities) && country.cities.length
            ? country.cities.flatMap(city => city.ops || [])
            : (country.ops || []);

          return {
            label: country.country_name,
            value: country.country_name,
            ops: opsList
              .filter(op => op?.ops)
              .map(op => ({
                label: op.ops_name || op.ops,
                value: `OPS::${op.ops}`,
              })),
          };
        });

        const allOption = { label: "ALL LOCATIONS", value: "ALL_LOCATIONS" };
        const flatValues = [
          allOption,
          ...groups.flatMap(g => [{ label: g.label, value: g.value }, ...g.ops]),
        ];

        setOffStreetCountries({ allOption, groups, flatValues });
      } else {
        setOffStreetCountriesError("Failed to load off-street countries");
      }
    } catch (err) {
      setOffStreetCountriesError("Failed to load off-street countries");
    } finally {
      setOffStreetCountriesLoading(false);
    }
  }, []);

  const fetchCurrentStatus = useCallback(async () => {
    try {
      setCurrentStatusLoading(true);
      setCurrentStatusError(null);
      const response = await apiCall("get", "/cp-map/data-off-street", null, null, false, false);
      if (response?.success && response?.data?.countries) {
        setCurrentStatusData(response.data.countries);
      } else {
        setCurrentStatusError("Failed to load current status");
      }
    } catch (err) {
      setCurrentStatusError("Failed to load current status");
    } finally {
      setCurrentStatusLoading(false);
    }
  }, []);

  const fetchTm1Metadata = useCallback(async () => {
    try {
      setTm1ServersLoading(true);
      setTm1ServersError(null);
      const response = await apiCall("GET", "/tm1/metadata", null, null, false, false);
      if (response?.success && Array.isArray(response?.data)) {
        setTm1Servers(response.data);
      } else {
        setTm1ServersError("Failed to load TM1 metadata");
      }
    } catch (err) {
      setTm1ServersError("Failed to load TM1 metadata");
    } finally {
      setTm1ServersLoading(false);
    }
  }, []);

  const fetchAllUsersWithPermissions = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const usersRes = await apiCall("GET", "/get-all-users?page=1&perPage=100", null, null);
      if (!usersRes?.success || !usersRes?.data) {
        throw new Error(usersRes?.message ?? "Failed to fetch users");
      }

      const permsRes = await apiCall("GET", "/access-control/all-users", null, null);

      const permsMap = {};
      if (permsRes?.success && Array.isArray(permsRes?.data)) {
        permsRes.data.forEach(item => {
          permsMap[item.user_id] = item.permissions ?? {};
        });
      }

      const merged = usersRes.data.map(u => ({
        id:       u.user_id,
        name:     u.full_name,
        role:     u.role_name ?? "Viewer",
        email:    u.email ?? "",
        username: u.username ?? "",
        initials: getInitials(u.full_name),
        perms:    mapApiPermsToInternal(permsMap[u.user_id] ?? {}),
      }));

      setUsers(merged);
    } catch (err) {
      setUsersError(err.message ?? "Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchAllUsersWithPermissions();
    fetchFinancialContracts();
    fetchPndCities();
    fetchOffStreetCountries();
    fetchCurrentStatus();
    fetchTm1Metadata();
  }, [fetchAllUsersWithPermissions, fetchFinancialContracts, fetchPndCities, fetchOffStreetCountries, fetchCurrentStatus, fetchTm1Metadata]);

  const MODULE_KEY_MAP = [
    { key: "pl",        module_name: "profit_&_loss" },
    { key: "bs",        module_name: "balance_sheet" },
    { key: "cf",        module_name: "cashflow"      },
    { key: "bp",        module_name: "boardpack"     },
    { key: "onstreet",  module_name: "onstreet"      },
    { key: "offstreet", module_name: "offstreet"     },
    { key: "current_status", module_name: "current_status" },
    { key: "cubes",     module_name: "tm1_cubes"     },
  ];

  function arraysEqual(a = [], b = []) {
    if (a.length !== b.length) return false;
    const sa = [...a].sort((x, y) => {
      if (typeof x === 'object' && x.name) {
        return x.name.localeCompare(y.name);
      }
      return String(x).localeCompare(String(y));
    });
    const sb = [...b].sort((x, y) => {
      if (typeof x === 'object' && x.name) {
        return x.name.localeCompare(y.name);
      }
      return String(x).localeCompare(String(y));
    });
    return sa.every((v, i) => {
      if (typeof v === 'object' && v.name) {
        return v.name === sb[i].name;
      }
      return v === sb[i];
    });
  }

  const handleSave = useCallback(async (uid, newPerms) => {
    const prevUser  = users.find(u => u.id === uid);
    const prevPerms = prevUser?.perms ?? mkEmptyPerms();

    setUsers(prev => prev.map(u => u.id === uid ? { ...u, perms: newPerms } : u));
    setIsSaving(true);

    try {
      const changedGroups = MODULE_KEY_MAP
        .map(({ key, module_name }) => ({
          module_name,
          contracts: [...new Set(newPerms[key] ?? [])],
          changed: !arraysEqual(prevPerms[key] ?? [], newPerms[key] ?? []),
        }))
        .filter(g => g.changed);

      if (changedGroups.length === 0) {
        toast.info("No changes to save");
        return;
      }

      const processedGroups = changedGroups.map(group => {
        if (group.module_name === "tm1_cubes") {
          const parsedContracts = group.contracts
            .map(c => {
              try {
                return JSON.parse(c);
              } catch {
                return null;
              }
            })
            .filter(Boolean)
            .map(c => {
              const server = tm1Servers.find(s => s.server === c.server);
              const cube = server?.data?.value?.find(cb => cb.Name === c.name);
              return { ...c, etag: cube?.["@odata.etag"] ?? null };
            });
          return {
            ...group,
            contracts: parsedContracts,
          };
        }
        
        if (group.module_name === "offstreet") {
          const formattedContracts = group.contracts.map(c => {
            if (typeof c === 'string') {
              return { name: c, parent: null };
            }
            return c;
          });
          return {
            ...group,
            contracts: formattedContracts,
          };
        }

        // Current Status - already in correct format
        if (group.module_name === "current_status") {
          return group;
        }
        
        return group;
      });

      await Promise.all(
        processedGroups.map(({ module_name, contracts }) =>
          apiCall("POST", "/access-control/upsert-bulk", 
            { user_id: uid, module_name, contracts }, 
            null, false, false
          )
        )
      );

      toast.success("Permissions saved successfully");

      const rawUser        = localStorage.getItem("user");
      const loggedInUserId = rawUser ? JSON.parse(rawUser)?.user_id : null;
      if (loggedInUserId === uid) fetchUserContracts();

    } catch (err) {
      console.error("Failed to save permissions:", err);
      toast.error("Failed to save permissions");
    } finally {
      setIsSaving(false);
    }
  }, [users, fetchUserContracts, tm1Servers]);

  const selectedUser = users.find(u => u.id === selectedUserId) ?? null;

  return (
    <div className="flex flex-col h-[calc(100vh-var(--header-height,64px))]">
      <div className="px-6 py-4 border-b border-border bg-card shrink-0">
        <h1 className="text-base font-semibold">User Access Control</h1>
        <p className="text-xs text-muted-foreground mt-0.5">Manage which contracts each user can access per module</p>
      </div>

      <div ref={containerRef} className="flex flex-1 min-h-0 overflow-hidden">
        <div style={{ width: leftWidth, minWidth: LEFT_MIN, maxWidth: LEFT_MAX }} className="shrink-0 overflow-hidden">
          <UserList
            users={users}
            selectedId={selectedUserId}
            onSelect={setSelectedUserId}
            loading={usersLoading}
            error={usersError}
            panelWidth={leftWidth}
          />
        </div>

        <ResizeHandle onResize={handleResize} />

        <div className="flex-1 min-w-0">
          {selectedUser ? (
            <PermissionPanel
              key={selectedUser.id}
              user={selectedUser}
              allUsers={users}
              financialContracts={financialContracts}
              financialContractsLoading={financialContractsLoading}
              financialContractsError={financialContractsError}
              pndCities={pndCities}
              pndCitiesLoading={pndCitiesLoading}
              pndCitiesError={pndCitiesError}
              offStreetCountries={offStreetCountries}
              offStreetCountriesLoading={offStreetCountriesLoading}
              offStreetCountriesError={offStreetCountriesError}
              currentStatusData={currentStatusData}
              currentStatusLoading={currentStatusLoading}
              currentStatusError={currentStatusError}
              tm1Servers={tm1Servers}
              tm1ServersLoading={tm1ServersLoading}
              tm1ServersError={tm1ServersError}
              onSave={handleSave}
              isSaving={isSaving}
            />
          ) : (
            <EmptyState />
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessControlPage;